/**
 * Frontend logger.
 * - Always logs to the browser console.
 * - Errors are also forwarded to the backend (/api/logs) so they appear
 *   in the server log files, even in production where DevTools aren't open.
 */
import { getStoredJson } from './storage';

const isDev = process.env.NODE_ENV === 'development';

const SENSITIVE_META_KEYS = ['token', 'authorization', 'password', 'secret', 'cookie', 'set-cookie'];

const sanitizeUrl = (value) => {
  if (typeof value !== 'string' || !value) return value;

  try {
    const url = new URL(value, window.location.origin);
    url.search = '';
    url.hash = '';
    return `${url.origin}${url.pathname}`;
  } catch {
    return value.split('?')[0].split('#')[0];
  }
};

const sanitizeMeta = (value, depth = 0) => {
  if (depth > 4) return '[Truncated]';
  if (Array.isArray(value)) return value.map((item) => sanitizeMeta(item, depth + 1));

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, nestedValue]) => {
      const isSensitive = SENSITIVE_META_KEYS.some((token) => key.toLowerCase().includes(token));
      acc[key] = isSensitive ? '[Redacted]' : sanitizeMeta(nestedValue, depth + 1);
      return acc;
    }, {});
  }

  if (typeof value === 'string') {
    return value.length > 1000 ? `${value.slice(0, 1000)}...` : value;
  }

  return value;
};

const sendToServer = (level, message, meta = {}) => {
  try {
    const user = getStoredJson('user');
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
      },
      body: JSON.stringify({
        level,
        message,
        meta: {
          ...sanitizeMeta(meta),
          url: sanitizeUrl(window.location.href),
          userAgent: navigator.userAgent,
        },
      }),
    }).catch(() => {}); // silently ignore — don't crash on log failure
  } catch (_) {}
};

const logger = {
  info: (message, meta) => {
    if (isDev) console.info('[INFO]', message, meta || '');
  },

  warn: (message, meta) => {
    console.warn('[WARN]', message, meta || '');
    sendToServer('warn', message, meta);
  },

  error: (message, errorOrMeta) => {
    const isError = errorOrMeta instanceof Error;
    const meta = isError
      ? { stack: errorOrMeta.stack, errorMessage: errorOrMeta.message }
      : errorOrMeta;

    console.error('[ERROR]', message, errorOrMeta || '');
    sendToServer('error', message, meta);
  },
};

export default logger;
